import type { CurriculumDisplayStatus, OrganizationCurriculumRow } from "@/types/database";

export type OrganizationCurriculumWithStatus = OrganizationCurriculumRow & {
  displayStatus: CurriculumDisplayStatus;
};

/** YYYY-MM-DD for "today" in UTC — matches Postgres DATE serialization for comparisons. */
export function calendarDateTodayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function dateKey(iso: string): string {
  return iso.slice(0, 10);
}

/** Sort: earliest start_date first, then created_at, then id (stable). */
function compareCurriculumRows(a: OrganizationCurriculumRow, b: OrganizationCurriculumRow): number {
  // Phase rows always have start_date; this comparator is only called for phase rows.
  const ad = a.start_date ? dateKey(a.start_date) : "";
  const bd = b.start_date ? dateKey(b.start_date) : "";
  if (ad !== bd) return ad.localeCompare(bd);
  if (a.created_at !== b.created_at) return a.created_at.localeCompare(b.created_at);
  return a.id.localeCompare(b.id);
}

/**
 * Orientation/Results are always-available library entries (no start_date, no scheduling).
 * They are returned via `extractIntroPhases` and excluded from active/upcoming/archived
 * partitioning by `assignCurriculumDisplayStatuses`.
 */
export function extractIntroPhases(rows: OrganizationCurriculumRow[]): {
  orientation: OrganizationCurriculumRow | null;
  results: OrganizationCurriculumRow | null;
  phaseRows: OrganizationCurriculumRow[];
} {
  let orientation: OrganizationCurriculumRow | null = null;
  let results: OrganizationCurriculumRow | null = null;
  const phaseRows: OrganizationCurriculumRow[] = [];
  for (const r of rows) {
    if (r.phase_type === "orientation") orientation = r;
    else if (r.phase_type === "results") results = r;
    else phaseRows.push(r);
  }
  return { orientation, results, phaseRows };
}

/**
 * Operates only on phase_type='phase' rows. Use `extractIntroPhases` first to peel off
 * orientation/results.
 *
 * - start_date after `today` → upcoming
 * - Among rows with start_date <= today, the latest by (start_date, created_at, id) → active
 * - Remaining past-dated rows → archived
 */
export function assignCurriculumDisplayStatuses(
  rows: OrganizationCurriculumRow[],
  today: string,
): OrganizationCurriculumWithStatus[] {
  const phaseRows = rows.filter((r) => r.phase_type === "phase" && r.start_date != null);
  const sorted = [...phaseRows].sort(compareCurriculumRows);
  let activeId: string | null = null;
  for (let i = sorted.length - 1; i >= 0; i--) {
    const row = sorted[i]!;
    if (row.start_date && dateKey(row.start_date) <= today) {
      activeId = row.id;
      break;
    }
  }
  return sorted.map((row) => {
    const dk = row.start_date ? dateKey(row.start_date) : "";
    let displayStatus: CurriculumDisplayStatus;
    if (dk > today) displayStatus = "upcoming";
    else if (activeId != null && row.id === activeId) displayStatus = "active";
    else displayStatus = "archived";
    return { ...row, displayStatus };
  });
}

function compareCurriculumAscending(
  a: OrganizationCurriculumWithStatus,
  b: OrganizationCurriculumWithStatus,
): number {
  const ad = a.start_date ? a.start_date.slice(0, 10) : "";
  const bd = b.start_date ? b.start_date.slice(0, 10) : "";
  if (ad !== bd) return ad.localeCompare(bd);
  if (a.created_at !== b.created_at) return a.created_at.localeCompare(b.created_at);
  return a.id.localeCompare(b.id);
}

/**
 * Splits rows by derived status. Upcoming: ascending by start_date (pipeline order).
 * Archive: descending (most recent past phase first). Active: at most one row.
 */
export function partitionCurriculumByStatus(rows: OrganizationCurriculumWithStatus[]): {
  active: OrganizationCurriculumWithStatus[];
  upcoming: OrganizationCurriculumWithStatus[];
  archived: OrganizationCurriculumWithStatus[];
} {
  const active: OrganizationCurriculumWithStatus[] = [];
  const upcoming: OrganizationCurriculumWithStatus[] = [];
  const archived: OrganizationCurriculumWithStatus[] = [];
  for (const r of rows) {
    if (r.displayStatus === "active") active.push(r);
    else if (r.displayStatus === "upcoming") upcoming.push(r);
    else archived.push(r);
  }
  upcoming.sort(compareCurriculumAscending);
  archived.sort((a, b) => compareCurriculumAscending(b, a));
  return { active, upcoming, archived };
}

/**
 * Returns the IDs of phase rows that have been superseded by a later phase whose start_date
 * has arrived. Used by lazy archival (§6.3): bundles owned by these phases should be archived.
 */
export function supersededPhaseIds(
  rows: OrganizationCurriculumRow[],
  today: string,
): string[] {
  const phaseRows = rows.filter((r) => r.phase_type === "phase" && r.start_date != null);
  const sorted = [...phaseRows].sort(compareCurriculumRows);
  let activeIndex = -1;
  for (let i = sorted.length - 1; i >= 0; i--) {
    const row = sorted[i]!;
    if (row.start_date && dateKey(row.start_date) <= today) {
      activeIndex = i;
      break;
    }
  }
  if (activeIndex <= 0) return [];
  return sorted.slice(0, activeIndex).map((r) => r.id);
}
